import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Download, MoreHorizontal } from 'lucide-react';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useInvoices } from '@/composables/financial/useInvoices';
import { useExport } from '@/composables/shared/useExport';
import { useDebounce } from '@/composables/shared/useDebounce';
import { openPdfInNewTab } from '@/lib/pdfViewer';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Invoice } from '@/types/table';

export function InvoiceListPage() {
  const navigate = useNavigate();
  const { invoices, meta, isLoading, filters, exportCsv, sendInvoice, duplicateInvoice, deleteInvoice, downloadPdf } = useInvoices();
  const { exportFile, isExporting } = useExport();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null);

  const handleDownloadPdf = async (invoice: Invoice) => {
    try {
      const blob = await downloadPdf(invoice.id);
      saveAs(blob, `invoice-${invoice.invoiceNumber || invoice.id}.pdf`);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (invoice: Invoice) => {
    try {
      await openPdfInNewTab(() => downloadPdf(invoice.id));
    } catch {
      toast.error('Failed to open PDF');
    }
  };

  const columns: ColumnDef<Invoice, unknown>[] = useMemo(() => [
    { accessorKey: 'invoiceNumber', header: ({ column }) => <DataTableColumnHeader column={column} title="Invoice #" />, cell: ({ row }) => <span className="font-medium">{row.original.invoiceNumber}</span> },
    { accessorKey: 'billingName', header: 'Client', cell: ({ row }) => row.original.billingName || '-' },
    { accessorKey: 'totalAmount', header: ({ column }) => <DataTableColumnHeader column={column} title="Total" />, cell: ({ row }) => formatCurrency(row.original.totalAmount, row.original.currency) },
    { accessorKey: 'paidAmount', header: 'Paid', cell: ({ row }) => formatCurrency(row.original.paidAmount, row.original.currency) },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={row.original.status} type="invoice" /> },
    { accessorKey: 'issueDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Issued" />, cell: ({ row }) => formatDate(row.original.issueDate) },
    { accessorKey: 'dueDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Due Date" />, cell: ({ row }) => formatDate(row.original.dueDate) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); sendInvoice.mutate(row.original.id); }}>Send</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateInvoice.mutate(row.original.id); }}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewPdf(row.original); }}>View PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadPdf(row.original); }}>Download PDF</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Invoices" description="Manage your invoices" actions={<><Button variant="outline" onClick={() => exportFile(exportCsv, 'invoices.csv')} disabled={isExporting}><Download className="mr-2 h-4 w-4" />Export</Button><Button onClick={() => navigate('/financial/invoices/new')}><Plus className="mr-2 h-4 w-4" />New Invoice</Button></>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search invoices..." />
      <DataTable columns={columns} data={invoices} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/financial/invoices/${row.id}`)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Invoice"
        description={`Are you sure you want to delete invoice ${deleteTarget?.invoiceNumber}? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteInvoice.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteInvoice.isPending}
      />
    </PageContainer>
  );
}
